import ITypedMap from '@/models/util/ITypedMap';
import RequestCache from '@/models/util/RequestCache';
import store from '@/store';
import TableProvider from './TableProvider';
import UiStringProvider, { IBulkRequestEntry } from './UiStringProvider';
import ICashShopEntry, { ICashShopTab } from '@/models/items/ICashShop';
import { mapCommodityRow, parseNeedJobClass, readTabs, sortEntries, tabNumToCategory } from '@/util/cashShop';

//  virt.cashcommodity is the merged commodity view (base cashcommodity +
//  cashcommodity_guild); always query the virt table.
const COMMODITY_TABLE = 'virt.cashcommodity';
const QUERY_LIMIT = 2000;

export const CASH_SHOP_PAGE_SIZE = 100;

export interface ICashShopEntryPage {
    entries: ICashShopEntry[];
    total: number;
}

export interface ICashShopProvider {
    getTabs(region?: string): Promise<ICashShopTab[]>;
    getEntriesPage(tabNum: number, subTabNum: number, includeOffSale: boolean, offset: number, region?: string): Promise<ICashShopEntryPage>;
    getEntry(sn: number, region?: string): Promise<ICashShopEntry | null>;
    getEntriesBySns(sns: number[], region?: string): Promise<ICashShopEntry[]>;
    getPackageMemberSns(sn: number, region?: string): Promise<number[]>;
    findByItemIds(itemIds: number[], region?: string): Promise<ICashShopEntry[]>;
    getNeedJobClasses(itemIds: number[], region?: string): Promise<ITypedMap<number[]>>;
}

class CashShopProvider implements ICashShopProvider {
    private _tabsCache: ITypedMap<ICashShopTab[]> = {};
    private _tabsReqCache = new RequestCache<ICashShopTab[]>();
    //  undefined = unknown, true = server pages+sorts, false = fallback keyset mode.
    private _pagedSupport: { [region: string]: boolean } = {};
    private _fullCache: ITypedMap<ICashShopEntry[]> = {};
    private _fullReqCache = new RequestCache<ICashShopEntry[]>();
    private _pageCache: ITypedMap<ICashShopEntryPage> = {};
    private _needJobCache: ITypedMap<number[]> = {};
    private _itemNameCache: ITypedMap<{ nameId: number; name: string; descriptionId: number; descriptionParams: string }> = {};
    private _packageSnsCache: ITypedMap<{ [sn: number]: boolean }> = {};
    private _packageSnsReqCache = new RequestCache<{ [sn: number]: boolean }>();

    public async getTabs(region?: string): Promise<ICashShopTab[]> {
        region = this._ensureRegion(region);
        const cached = this._tabsCache[region];
        if (cached) {
            return cached;
        }

        return this._tabsReqCache.tryCache(region, async () => {
            const rows = await TableProvider.getTable<any>('cashtabtable', region);
            const tabs = readTabs(rows);

            const requests: IBulkRequestEntry[] = [];
            for (const tab of tabs) {
                if (tab.nameId) {
                    requests.push({ id: tab.nameId });
                }
                for (const sub of tab.subTabs) {
                    requests.push({ id: sub.nameId });
                }
            }

            const resolved = await UiStringProvider.getBulk(requests, region);
            for (const tab of tabs) {
                tab.name = resolved[tab.nameId]?.message || `#${tab.nameId}`;
                for (const sub of tab.subTabs) {
                    sub.name = resolved[sub.nameId]?.message || `#${sub.nameId}`;
                }
            }

            this._tabsCache[region!] = tabs;
            return tabs;
        });
    }

    public async getEntriesPage(tabNum: number, subTabNum: number, includeOffSale: boolean, offset: number, region?: string): Promise<ICashShopEntryPage> {
        region = this._ensureRegion(region);
        const baseKey = `${region}:${tabNum}:${subTabNum}:${includeOffSale ? 1 : 0}`;

        if (this._pagedSupport[region] !== false) {
            const pageKey = `${baseKey}:${offset}`;
            const cached = this._pageCache[pageKey];
            if (cached) {
                return cached;
            }

            const res = await TableProvider.queryTablePaged<any>(COMMODITY_TABLE, this._entriesQuery(tabNum, subTabNum, includeOffSale), region, {
                sort: '_Priority;desc',
                rowoffset: offset,
                limit: CASH_SHOP_PAGE_SIZE,
                uiresolve: ['_NameID'],
            });

            if (res.total !== null) {
                this._pagedSupport[region] = true;
                //  Server order is authoritative — no client re-sort.
                const entries = res.rows.map(mapCommodityRow);
                await this._enrichEntries(entries, region);
                const page: ICashShopEntryPage = { entries, total: res.total };
                this._pageCache[pageKey] = page;
                return page;
            }

            //  No X-Total-Count: the server ignored sort/rowoffset (Java
            //  STARGAZER), so this response's contents are unusable for paging.
            this._pagedSupport[region] = false;
        }

        const full = await this._getAllEntries(baseKey, tabNum, subTabNum, includeOffSale, region);
        return { entries: full.slice(offset, offset + CASH_SHOP_PAGE_SIZE), total: full.length };
    }

    private _entriesQuery(tabNum: number, subTabNum: number, includeOffSale: boolean): string {
        let q = `_Category;eq;${tabNumToCategory(tabNum)}$_SubCategory;eq;${subTabNum}`;
        if (!includeOffSale) {
            q += '$_OnSale;eq;1';
        }

        return q;
    }

    //  Fallback: exhaustive SN-keyset fetch (row id == _SN on virt.cashcommodity),
    //  sorted client-side. Complete regardless of the server's 2000-row cap.
    private async _getAllEntries(key: string, tabNum: number, subTabNum: number, includeOffSale: boolean, region: string): Promise<ICashShopEntry[]> {
        const cached = this._fullCache[key];
        if (cached) {
            return cached;
        }

        return this._fullReqCache.tryCache(key, async () => {
            const base = this._entriesQuery(tabNum, subTabNum, includeOffSale);
            const out: any[] = [];
            let lastSn = 0;
            for (let i = 0; i < 50; i++) {
                const rows = await TableProvider.queryTable<any>(COMMODITY_TABLE, `${base}$_SN;gt;${lastSn}`, region, {
                    uiresolve: ['_NameID'],
                    limit: QUERY_LIMIT,
                });
                out.push(...rows);
                if (rows.length < QUERY_LIMIT) {
                    break;
                }

                lastSn = rows[rows.length - 1]._SN;
            }

            //  Composite tables (base + guild) can yield duplicate SNs; keep the
            //  first occurrence.
            const seen: { [sn: number]: boolean } = {};
            const mapped = out.map(mapCommodityRow).filter((e) => {
                if (seen[e.sn]) {
                    return false;
                }
                seen[e.sn] = true;
                return true;
            });
            //  Resolve inherited names before sorting so the name tiebreak uses
            //  the display name.
            await this._enrichEntries(mapped, region);
            const entries = sortEntries(mapped);
            this._fullCache[key] = entries;
            return entries;
        });
    }

    public async getEntry(sn: number, region?: string): Promise<ICashShopEntry | null> {
        region = this._ensureRegion(region);
        const rows = await TableProvider.queryTable<any>(COMMODITY_TABLE, `_SN;eq;${sn}`, region, {
            uiresolve: ['_NameID', '_DescriptionID'],
            limit: 2,
        });

        if (!rows.length) {
            return null;
        }

        const entry = mapCommodityRow(rows[0]);
        await this._enrichEntries([entry], region);
        return entry;
    }

    public async getEntriesBySns(sns: number[], region?: string): Promise<ICashShopEntry[]> {
        if (!sns.length) {
            return [];
        }

        region = this._ensureRegion(region);
        const rows = await TableProvider.queryTable<any>(COMMODITY_TABLE, `_SN;in;${sns.join(',')}`, region, {
            uiresolve: ['_NameID'],
            limit: QUERY_LIMIT,
        });

        const bySn: ITypedMap<ICashShopEntry> = {};
        for (const row of rows) {
            const entry = mapCommodityRow(row);
            bySn[entry.sn] = entry;
        }

        const entries = sns.map((sn) => bySn[sn]).filter((e) => !!e);
        await this._enrichEntries(entries, region);
        return entries;
    }

    public async getPackageMemberSns(sn: number, region?: string): Promise<number[]> {
        region = this._ensureRegion(region);
        const rows = await TableProvider.queryTable<any>('cashpackagetable', `_SN;eq;${sn}`, region, {
            limit: 2,
        });
        if (!rows.length) {
            return [];
        }

        const out: number[] = [];
        for (let i = 0; i <= 19; i++) {
            const member = Number(rows[0]['_CommodityID' + String(i).padStart(2, '0')]);
            if (member) {
                out.push(member);
            }
        }

        return out;
    }

    public async findByItemIds(itemIds: number[], region?: string): Promise<ICashShopEntry[]> {
        if (!itemIds.length) {
            return [];
        }

        region = this._ensureRegion(region);
        //  Chunk to keep query URLs bounded.
        const out: ICashShopEntry[] = [];
        for (let i = 0; i < itemIds.length; i += 50) {
            const chunk = itemIds.slice(i, i + 50);
            const rows = await TableProvider.queryTable<any>(COMMODITY_TABLE, `_ItemID01;in;${chunk.join(',')}`, region, {
                uiresolve: ['_NameID'],
                limit: QUERY_LIMIT,
            });
            out.push(...rows.map(mapCommodityRow));
        }

        await this._enrichEntries(out, region);
        return sortEntries(out);
    }

    //  Batched _NeedJobClass lookup for class filtering: itemId -> job id list
    //  ([] = usable by every class, including items with no itemtable row).
    public async getNeedJobClasses(itemIds: number[], region?: string): Promise<ITypedMap<number[]>> {
        region = this._ensureRegion(region);
        const ret: ITypedMap<number[]> = {};
        const missing: number[] = [];
        for (const id of itemIds) {
            const cached = this._needJobCache[`${region}:${id}`];
            if (cached) {
                ret[id] = cached;
            } else if (id) {
                missing.push(id);
            }
        }

        for (let i = 0; i < missing.length; i += 100) {
            const chunk = missing.slice(i, i + 100);
            let rows: ITypedMap<any> = {};
            try {
                if (chunk.length === 1) {
                    //  A single id returns the bare row, not an id-keyed map.
                    rows = { [chunk[0]]: await TableProvider.getTableRow<any>('virt.itemtable', chunk[0], region, { select: ['_NeedJobClass'] as any }) };
                } else {
                    rows = await TableProvider.getTableRows<any>('virt.itemtable', chunk, region, { select: ['_NeedJobClass'] as any });
                }
            } catch (e) {
                //  Missing rows fall through to the usable-by-all default below.
            }

            for (const id of chunk) {
                const row = rows[id];
                const parsed = parseNeedJobClass(row ? String(row._NeedJobClass || '') : '');
                this._needJobCache[`${region}:${id}`] = parsed;
                ret[id] = parsed;
            }
        }

        return ret;
    }

    //  Post-mapping enrichment shared by every entry-producing path.
    private async _enrichEntries(entries: ICashShopEntry[], region: string): Promise<void> {
        await Promise.all([
            this._resolveInheritedNames(entries, region),
            this._resolvePackageFlags(entries, region),
        ]);
    }

    //  Game rule (user-confirmed): an entry is a package iff cashpackagetable
    //  has a row for its SN — _ItemSort is wrong on some rows (e.g. 7992485).
    private async _resolvePackageFlags(entries: ICashShopEntry[], region: string): Promise<void> {
        if (!entries.length) {
            return;
        }

        let sns = this._packageSnsCache[region];
        if (!sns) {
            sns = await this._packageSnsReqCache.tryCache(region, async () => {
                const rows = await TableProvider.getTableRowsOffset<any>('cashpackagetable', 0, region, {
                    select: ['_SN'] as any,
                    limit: 10000,
                });
                const set: { [sn: number]: boolean } = {};
                for (const row of rows) {
                    const sn = Number(row._SN);
                    if (sn) {
                        set[sn] = true;
                    }
                }

                this._packageSnsCache[region] = set;
                return set;
            });
        }

        for (const e of entries) {
            e.isPackage = !!sns[e.sn];
        }
    }

    //  Game behavior: _NameID/_DescriptionID of -1 mean "use the first granted
    //  item's name/description". Patch such entries in place from virt.itemtable.
    private async _resolveInheritedNames(entries: ICashShopEntry[], region: string): Promise<void> {
        const pending = entries.filter((e) => (e.nameId === -1 || e.descriptionId === -1) && e.itemIds.length > 0);
        if (!pending.length) {
            return;
        }

        const missing = [...new Set(pending
            .map((e) => e.itemIds[0])
            .filter((id) => !this._itemNameCache[`${region}:${id}`]))];

        for (let i = 0; i < missing.length; i += 100) {
            const chunk = missing.slice(i, i + 100);
            //  Dotted uiresolve pairs make the server fill uistring params from
            //  the sibling *Param column (e.g. "Lv. {level} ..." templates).
            const opt = {
                select: ['_NameID', '_DescriptionID', '_NameIDParam', '_DescriptionIDParam'] as any,
                uiresolve: ['_NameID._NameIDParam'] as any,
            };
            let rows: ITypedMap<any> = {};
            try {
                if (chunk.length === 1) {
                    //  A single id returns the bare row, not an id-keyed map.
                    rows = { [chunk[0]]: await TableProvider.getTableRow<any>('virt.itemtable', chunk[0], region, opt) };
                } else {
                    rows = await TableProvider.getTableRows<any>('virt.itemtable', chunk, region, opt);
                }
            } catch (e) {
                //  Missing rows fall through to the empty default below.
            }

            for (const id of chunk) {
                const row = rows[id];
                this._itemNameCache[`${region}:${id}`] = {
                    nameId: row ? Number(row._NameID) || 0 : 0,
                    name: row ? String(row._NameID_txt || '') : '',
                    descriptionId: row ? Number(row._DescriptionID) || 0 : 0,
                    descriptionParams: row ? String(row._DescriptionIDParam || '') : '',
                };
            }
        }

        for (const e of pending) {
            const inherited = this._itemNameCache[`${region}:${e.itemIds[0]}`];
            if (!inherited) {
                continue;
            }
            if (e.nameId === -1) {
                e.nameId = inherited.nameId;
                e.name = inherited.name;
            }
            if (e.descriptionId === -1) {
                e.descriptionId = inherited.descriptionId;
                e.descriptionParams = inherited.descriptionParams;
            }
        }
    }

    private _ensureRegion(region?: string): string {
        if (!region) {
            return store.state.regionCode;
        }

        return region;
    }
}

export default new CashShopProvider() as ICashShopProvider;
